/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';

import DeliverableHeader from '../header-v2/DeliverableHeader';
import DeliverableTimeline from '../deliverable-timeline/DeliverableTimeline';
import TabsSection from 'components/tabs/tabs-section/TabsSection';
import DeliverableInformationTab from './deliverable-information-tab/DeliverableInformationTab';
import DeliverableContentApprovalTab from './deliverable-content-approval-tab/DeliverableContentApprovalTab';
import DeliverableInsightsTab from './deliverable-insights-tab/DeliverableInsightsTab';
import DeliverableUploadTab from './deliverable-upload-tab/DeliverableUploadTab';
import CreatorInfoWithChannels from 'components/creator-info-with-channels/CreatorInfoWithChannels';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { getClientApprovalMedia } from 'modules/deliverables/reducers/deliverableContentApproval';

import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO,
  DELIVERABLE_TYPE_OTHER,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  scrollToDeliverable
} from 'utils/deliverables';
import { isInfluencerProfileRejected } from 'utils/profile';
import { APPROVAL_STATUSES } from 'utils/contentApproval';
import { replaceHistoryStateWithDefault } from 'utils/helper/common';

applyTranslations(require('./deliverable-v2.i18n.json'));

import type {
  Deliverable as DeliverableAlias,
  DeliverableAttachment,
  DeliverableClientApproval,
  DeliverableShort,
  DeliverableUploadedEventAction,
  DeliverableV2TabKey,
  DeliverableWithContentApproval,
  InfluencerMetrics,
  RealmOutfit,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';

import './deliverable-v2.scss';

type OwnProps = {
  selectedTab: DeliverableV2TabKey,
  deliverable: DeliverableAlias,
  subcampaign: Subcampaign,
  isForBrand: boolean,
  isExpanded: boolean,
  isCcm?: boolean,
  deliverablesWithCA2: Array<DeliverableShort>,
  influencerProfile?: SubcampaignInfluencerProfile,
  isContentPage?: boolean,
  onContentMoved: () => void,
  onViewChange: (selectedView: DeliverableV2TabKey) => void,
  onExpanded: () => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void,
  onSelectMoveOutfitToAnotherDeliverable?: (outfit: RealmOutfit, checked: boolean) => void
};

type StateProps = {
  influencersMetrics: InfluencerMetrics,
  clientApprovalData: DeliverableClientApproval
};

type DispatchProps = {
  dispatchShowConfirmationModal: (
    params: {
      title?: string,
      message?: string,
      onCancel?: () => void,
      onClose?: () => void,
      innerContent?: React.ReactNode,
      confirmButtonLabel: string,
      onProceed?: () => void,
      onConfirm?: () => void,
      proceedButtonLabel?: string
    }
  ) => void,
  dispatchGetClientApprovalMedia: (offerId: number, deliverableId: number) =>
    Promise<DeliverableClientApproval>,
  dispatchHideConfirmationModal: () => void
}

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  isModified: boolean,
  contentApprovalView: 'default' | 'client-approval' | 'move-content'
};

type TabType = {
  information: DeliverableV2TabKey,
  content_approval: DeliverableV2TabKey,
  insights: DeliverableV2TabKey,
  files: DeliverableV2TabKey
};

type TabItem = {
  label: string,
  identifier: DeliverableV2TabKey
};

const msgKey = 'deliverable_v2';

const TAB_TYPE: TabType = {
  information: 'information',
  content_approval: 'content_approval',
  insights: 'insights',
  files: 'files'
};

const TABS_ITEMS: Array<TabItem> = [
  {
    label: __t(`${msgKey}.labels.${TAB_TYPE.information}`),
    identifier: TAB_TYPE.information
  },
  {
    label: __t(`${msgKey}.labels.${TAB_TYPE.content_approval}`),
    identifier: TAB_TYPE.content_approval
  },
  {
    label: __t(`${msgKey}.labels.${TAB_TYPE.insights}`),
    identifier: TAB_TYPE.insights
  },
  {
    label: __t(`${msgKey}.labels.${TAB_TYPE.files}`),
    identifier: TAB_TYPE.files
  }
];

class Deliverable extends React.Component<Props, State> {
  deliverablesContainerRef: React.RefObject<any> = React.createRef();

  constructor(props: Props) {
    super(props);

    this.state = {
      isModified: false,
      contentApprovalView: 'default'
    };
  }

  componentDidMount() {
    const { subcampaign, deliverable, dispatchGetClientApprovalMedia } = this.props;
    const { offer } = subcampaign;

    dispatchGetClientApprovalMedia(offer.id, deliverable.id);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { subcampaign, deliverable, dispatchGetClientApprovalMedia } = this.props;
    const { contentApprovalView } = this.state;
    const { offer } = subcampaign;

    if (contentApprovalView !== prevState.contentApprovalView && contentApprovalView === 'client-approval') {
      dispatchGetClientApprovalMedia(offer.id, deliverable.id);
    }
  }

  getUpdatedSelectedTab(): DeliverableV2TabKey {
    const { deliverable, selectedTab } = this.props;
    const tabs = this.getTabs();
    const hasMatchingTab = tabs.some(tab => selectedTab === tab.identifier);
    let updatedSelectedTab = hasMatchingTab && selectedTab ? selectedTab : TAB_TYPE.information;

    if (!hasMatchingTab && deliverable.deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT) {
      updatedSelectedTab = TAB_TYPE.content_approval;
    } else if (!hasMatchingTab) {
      updatedSelectedTab = selectedTab === TAB_TYPE.insights ? TAB_TYPE.files : TAB_TYPE.information;
    }

    return updatedSelectedTab;
  }

  getTabs(): Array<TabItem> {
    const { deliverable } = this.props;
    const { requiresApproval, deliverableType } = deliverable;

    if (
      deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT ||
      deliverableType === DELIVERABLE_TYPE_ENTERTAINMENT_VIDEO
    ) {
      return this.getTabsItems().filter(item => (
        item.identifier === TAB_TYPE.information
        || (deliverable.requiresApproval && item.identifier === TAB_TYPE.content_approval)
      ));
    }

    if (deliverableType === DELIVERABLE_TYPE_OTHER) {
      return this.getTabsItems().filter(item => (
        item.identifier === TAB_TYPE.information
        || item.identifier === TAB_TYPE.files
        || (deliverable.requiresApproval && item.identifier === TAB_TYPE.content_approval)
      ));
    }

    return this.getTabsItems().filter(item => (
      !(item.identifier === TAB_TYPE.content_approval && !requiresApproval)
      && item.identifier !== TAB_TYPE.files
    ));
  }

  getTabsItems = (): Array<TabItem> => {
    const { contentApprovalView } = this.state;

    if (contentApprovalView === 'client-approval') {
      return this.getTabItemsWithClientApproval();
    }

    if (contentApprovalView === 'move-content') {
      return this.getTabItemsWithMoveContent();
    }

    return TABS_ITEMS;
  };

  getTabItemsWithClientApproval = (): Array<TabItem> => (
    TABS_ITEMS.map(item => {
      if (item.identifier === TAB_TYPE.content_approval) {
        return {
          ...item,
          label: __t(`${msgKey}.labels.content_approval_with_release`),
          onClose: this.handleClientApprovalClose
        };
      }

      return item;
    })
  );

  getTabItemsWithMoveContent = (): Array<TabItem> => (
    TABS_ITEMS.map(item => {
      if (item.identifier === TAB_TYPE.content_approval) {
        return {
          ...item,
          label: __t(`${msgKey}.labels.content_approval_with_move`),
          onClose: this.handleContentMoveClose
        };
      }

      return item;
    })
  );

  getHasClientProvidedCaptionFeedbackStatus = (): boolean => {
    const { deliverable, clientApprovalData } = this.props;
    const { contentApproval } = deliverable;
    const { captionStatus } = clientApprovalData || {};
    const updatedCaptionStatus = captionStatus || APPROVAL_STATUSES.PENDING;

    let hasCaptionStatusChanged = false;

    if (!isNil(contentApproval) && updatedCaptionStatus !== APPROVAL_STATUSES.PENDING) {
      hasCaptionStatusChanged = contentApproval.caption.approvalStatus !== updatedCaptionStatus;
    }

    return hasCaptionStatusChanged;
  };

  getIsPublishedToClientStatus = (asset: DeliverableAttachment): boolean => {
    const { clientApprovalData } = this.props;
    const { items = [] } = clientApprovalData || {};
    const foundItem = items.find(item => item.assetId === asset.id);

    return Boolean(foundItem);
  };

  getHasClientProvidedAttachmentFeedbackStatus(asset: DeliverableAttachment): boolean {
    const { clientApprovalData } = this.props;
    const { items = [] } = clientApprovalData || {};

    const hasAttachmentsStatusChanged = items.some(item => (
      item.status !== APPROVAL_STATUSES.PENDING
      && item.assetId === asset.id
      && item.status !== asset.approvalStatus
    ));

    return hasAttachmentsStatusChanged;
  }

  getAttachmentsWithClientFeedback = (): Array<DeliverableAttachment> => {
    const { deliverable } = this.props;

    return (deliverable.contentApproval?.assets || []).map(asset => ({
      ...asset,
      hasClientProvidedFeedback: this.getHasClientProvidedAttachmentFeedbackStatus(asset),
      isPublishedToClient: this.getIsPublishedToClientStatus(asset)
    }));
  };

  showTabChangeModal = (cb) => {
    const { dispatchShowConfirmationModal, dispatchHideConfirmationModal } = this.props;

    dispatchShowConfirmationModal({
      title: __t(`${msgKey}.discard_changes_modal.title`),
      message: __t(`${msgKey}.discard_changes_modal.info`),
      confirmButtonLabel: __t(`${msgKey}.discard_changes_modal.discard_btn`),
      onClose: () => dispatchHideConfirmationModal(),
      onCancel: () => dispatchHideConfirmationModal(),
      onConfirm: () => {
        dispatchHideConfirmationModal();
        cb();
      },
      innerContent: (
        <div className="deliverable-content-approval-tab__modal-info">
          {__t(`${msgKey}.discard_changes_modal.confirmation_message`)}
        </div>
      )
    });
  };

  handleClientApprovalClose = (event) => {
    event.stopPropagation();

    const { isModified } = this.state;

    if (isModified) {
      this.showTabChangeModal(() => {
        this.handleOpenClientApproval(false);
        this.handleModification(false);
      });
    } else {
      this.handleOpenClientApproval(false);
    }
  };

  handleContentMoveClose = (event) => {
    event.stopPropagation();

    const { isModified } = this.state;

    if (isModified) {
      this.showTabChangeModal(() => {
        this.handleOpenMoveContent(false);
        this.handleModification(false);
      });
    } else {
      this.handleOpenMoveContent(false);
    }
  };

  handleTabChanged = (tab?: DeliverableV2TabKey): void => {
    const { isModified } = this.state;

    if (tab && Object.values(TAB_TYPE).indexOf(tab) > -1) {
      if (isModified) {
        this.showTabChangeModal(() => this.handleTabState(tab));
      } else {
        this.handleTabState(tab);
      }
    }
  };

  handleTabState(tab: DeliverableV2TabKey): void {
    const { onExpanded, onViewChange, isExpanded } = this.props;

    replaceHistoryStateWithDefault();

    onViewChange(tab);

    if (onExpanded && !isExpanded) {
      onExpanded();
    }
  }

  handleModification = (isModified: boolean): void => {
    this.setState(prevState => ({
      ...prevState,
      isModified
    }));
  };

  handleExpanded = (): void => {
    const { onExpanded } = this.props;

    if (onExpanded) {
      onExpanded();
    }
  };

  onContentApprovalViewChange = () => {
    const { deliverable, isExpanded } = this.props;
    const { contentApprovalView } = this.state;

    if (contentApprovalView !== 'default' && isExpanded) {
      scrollToDeliverable(deliverable.id, 'deliverable-v2');
    }
  };

  handleOpenClientApproval = (openClientApproval?: boolean): void => {
    this.setState((prevState) => ({
      ...prevState,
      contentApprovalView: openClientApproval ? 'client-approval' : 'default'
    }), this.onContentApprovalViewChange);
  };

  handleContentMoved = async (): Promise<void> => {
    const { onContentMoved } = this.props;

    await onContentMoved();

    this.setState((prevState) => ({
      ...prevState,
      contentApprovalView: 'default'
    }), this.onContentApprovalViewChange);
  };

  handleOpenMoveContent = (showMoveContent: boolean): void => {
    this.setState((prevState) => ({
      ...prevState,
      contentApprovalView: showMoveContent ? 'move-content' : 'default'
    }), this.onContentApprovalViewChange);
  };

  renderTab = (): React.ReactNode => {
    const {
      deliverable,
      deliverablesWithCA2,
      subcampaign,
      isForBrand,
      isCcm,
      clientApprovalData,
      isContentPage,
      onTrackDeliverableUploadEvent,
      onSelectMoveOutfitToAnotherDeliverable
    } = this.props;
    const { contentApprovalView } = this.state;
    const updatedSelectedTab = this.getUpdatedSelectedTab();

    if (updatedSelectedTab === 'content_approval' && deliverable.requiresApproval) {
      return (
        <DeliverableContentApprovalTab
          deliverable={deliverable as DeliverableWithContentApproval}
          subcampaign={subcampaign}
          isForBrand={isForBrand}
          isCcm={isCcm}
          deliverablesWithCA2={deliverablesWithCA2}
          postAttachments={this.getAttachmentsWithClientFeedback()}
          clientApprovalData={clientApprovalData}
          setDeliverableModified={this.handleModification}
          contentApprovalView={contentApprovalView}
          getIsPublishedToClientStatus={this.getIsPublishedToClientStatus}
          hasClientProvidedCaptionFeedback={this.getHasClientProvidedCaptionFeedbackStatus}
          onContentMoved={this.handleContentMoved}
          onOpenMoveContent={this.handleOpenMoveContent}
          onOpenClientApproval={this.handleOpenClientApproval}
          onTrackDeliverableUploadEvent={onTrackDeliverableUploadEvent}
          onSelectMoveOutfitToAnotherDeliverable={onSelectMoveOutfitToAnotherDeliverable}
        />
      );
    }

    if (updatedSelectedTab === 'insights') {
      return (
        <DeliverableInsightsTab
          deliverable={deliverable}
          isForBrand={isForBrand}
          postAttachments={deliverable.postAttachments || []}
          subcampaign={subcampaign}
          setDeliverableModified={this.handleModification}
          onTrackDeliverableUploadEvent={onTrackDeliverableUploadEvent}
          isContentPage={isContentPage}
        />
      );
    }

    if (updatedSelectedTab === 'files') {
      return (
        <DeliverableUploadTab
          deliverable={deliverable}
          isForBrand={isForBrand}
          postAttachments={deliverable.postAttachments || []}
          subcampaign={subcampaign}
          setDeliverableModified={this.handleModification}
          onTrackDeliverableUploadEvent={onTrackDeliverableUploadEvent}
          isContentPage={isContentPage}
        />
      );
    }

    return (
      <DeliverableInformationTab
        deliverable={deliverable}
        isCcm={isCcm}
      />
    );
  };

  private renderOverlay(): React.ReactNode {
    const { subcampaign } = this.props;
    const isViewForRejectedInfluencerProfile = isInfluencerProfileRejected(subcampaign.creatorProfile);
    const showOverlay = isViewForRejectedInfluencerProfile || subcampaign.state === 'revoked';

    if (showOverlay && this.getUpdatedSelectedTab() !== TAB_TYPE.information) {
      return (
        <div className="deliverable-v2__tab-overlay" />
      );
    }

    return null;
  }

  render(): React.ReactNode {
    const { deliverable, isForBrand, isExpanded, influencerProfile, influencersMetrics, subcampaign } = this.props;
    const tabs = this.getTabs();
    const updatedSelectedTab = this.getUpdatedSelectedTab();

    return (
      <div>
        {
          influencerProfile && influencersMetrics[influencerProfile.id] && (
            <div className="deliverable-header__influencer">
              <CreatorInfoWithChannels
                creatorProfile={influencerProfile}
                metrics={influencersMetrics[influencerProfile.id]}
                includeRatio
              />
            </div>
          )
        }
        <section className="deliverable-v2">
          <div className="deliverable-v2__header">
            <DeliverableHeader
              deliverable={deliverable}
              mode="view"
              isExpanded={isExpanded}
              onExpanded={this.handleExpanded}
            />
          </div>
          <DeliverableTimeline
            deliverable={deliverable}
            isForBrand={isForBrand}
            subcampaignState={subcampaign.state}
          />
          {/* id is required to auto-scroll when selecting "release content to client" */}
          <div id={`deliverable-v2-${deliverable.id}`}>
            <TabsSection
              items={tabs}
              activeTab={updatedSelectedTab}
              clickableActiveTab
              className="deliverable-v2__tab-section"
              onClick={(tab) => this.handleTabChanged(tab as DeliverableV2TabKey)}
            />
          </div>
          {
            isExpanded && (
              <div className="deliverable-v2__tab-overlay-container">
                {this.renderTab()}
                {this.renderOverlay()}
              </div>
            )
          }
        </section>
      </div>
    );
  }
}

const mapStateToProps = ({ deliverableContentApproval, influencersMetrics }, ownProps: OwnProps): StateProps => ({
  influencersMetrics: influencersMetrics.data,
  clientApprovalData: deliverableContentApproval.clientApprovalData[ownProps.deliverable.id]
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchGetClientApprovalMedia: (offerId: number, deliverableId: number) =>
    dispatch(getClientApprovalMedia(offerId, deliverableId)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(Deliverable);
